import { type InvestmentExportConverterType, IntegrationsControllerV3ApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { axiosExtract, backendErrorMessage } from "$root/third-party-integrations/axios";
import { downloadContentDisposition, parallelize, ToastableError } from "$root/utils";

/** A conversion the backend refused with a message: the user reads that message, its "- " lines as a list. */
export async function rethrowConversionRefusal(error: unknown): Promise<never> {
	const message = await backendErrorMessage(error);
	throw message
		? new ToastableError(message, { cause: error, icon: "Portfolio", msg: String(error), listLines: true })
		: error;
}

/** Any other export failure becomes the generic message; a refusal already turned into one passes through. */
export function exportFailure(error: unknown): ToastableError {
	return error instanceof ToastableError
		? error
		: new ToastableError("Unable to export investments", { cause: error, icon: "Portfolio", msg: String(error) });
}

const useCompositionDownload = () => {
	const integrationsApi = useApiGen(IntegrationsControllerV3ApiFactory);

	async function downloadInvestmentConversion(
		format: InvestmentExportConverterType,
		uuid: string,
		enhancement?: boolean,
		persist?: boolean,
	) {
		try {
			const composition = await axiosExtract(integrationsApi.exportInvestment(uuid, enhancement ?? false));
			const response = await integrationsApi
				.convertTo(format, [composition], persist, { responseType: "blob" })
				.catch(rethrowConversionRefusal);
			await downloadContentDisposition(response);
		} catch (error) {
			throw exportFailure(error);
		}
	}

	async function scheduleMultiInvestmentsConversion(
		format: InvestmentExportConverterType,
		list: Array<{ uuid: string; enhancement?: boolean }>,
		persist: boolean = true,
	) {
		const exportInvestmentCalls = list.map(
			(investment) => () =>
				axiosExtract(integrationsApi.exportInvestment(investment.uuid, investment.enhancement ?? false)),
		);
		const compositions = await parallelize(exportInvestmentCalls, { concurrency: 3 });
		await integrationsApi.convertTo(format, compositions, persist);
	}

	return { downloadInvestmentConversion, scheduleMultiInvestmentsConversion };
};

export default useCompositionDownload;
